// app/src/lib/datetime.ts — single source of truth for rendering dates/times in the UI.
//
// Every timestamp shown to a user is formatted in the ORGANISATION'S configured
// timezone (Settings → Business → Timezone), never the browser's. The chosen zone is
// cached in localStorage so it is correct on first paint, before any network call;
// loadReceiptConfig() and the Settings save handler keep it fresh.

const TZ_KEY = "epos.tz";

function readCachedTz(): string {
  try {
    const direct = localStorage.getItem(TZ_KEY);
    if (direct) return direct;
    // Fall back to the timezone embedded in the cached receipt config.
    const cfg = localStorage.getItem("epos.receipt.cfg");
    if (cfg) {
      const tz = JSON.parse(cfg)?.timezone;
      if (tz) return tz;
    }
  } catch {
    /* ignore */
  }
  return "Europe/London";
}

let appTz: string = typeof localStorage !== "undefined" ? readCachedTz() : "Europe/London";

/** Update the timezone used everywhere in the UI. Call when org settings load or change. */
export function setAppTimezone(tz?: string | null): void {
  if (!tz || tz === appTz) return;
  appTz = tz;
  try {
    localStorage.setItem(TZ_KEY, tz);
  } catch {
    /* ignore */
  }
}

/** The IANA timezone currently used for all UI date/time formatting. */
export function appTimezone(): string {
  return appTz;
}

function fmt(ms: number | null | undefined, opts: Intl.DateTimeFormatOptions): string {
  if (ms == null) return "—";
  const n = Number(ms);
  if (!Number.isFinite(n)) return "—";
  try {
    return new Intl.DateTimeFormat("en-GB", { timeZone: appTz, ...opts }).format(new Date(n));
  } catch {
    // Invalid IANA zone — fall back to the browser default rather than throwing.
    return new Intl.DateTimeFormat("en-GB", opts).format(new Date(n));
  }
}

const DATETIME: Intl.DateTimeFormatOptions = { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" };
const DATE: Intl.DateTimeFormatOptions = { day: "2-digit", month: "2-digit", year: "numeric" };
const TIME: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit" };

/** Date + time in the shop timezone (default: "02 Jan, 14:30"). */
export const fmtDateTime = (ms: number | null | undefined, opts?: Intl.DateTimeFormatOptions): string => fmt(ms, opts ?? DATETIME);
/** Date only in the shop timezone (default: "02/01/2026"). */
export const fmtDate = (ms: number | null | undefined, opts?: Intl.DateTimeFormatOptions): string => fmt(ms, opts ?? DATE);
/** Time only in the shop timezone (default: "14:30"). */
export const fmtTime = (ms: number | null | undefined, opts?: Intl.DateTimeFormatOptions): string => fmt(ms, opts ?? TIME);
